import { doc, setDoc } from "firebase/firestore";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Flame,
  Sparkles,
  Target,
  AlertTriangle,
} from "lucide-react";

/* ======================================================
   UTILS
====================================================== */
const getLocalDateKey = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const groupDaysByMonth = (days) => {
  const map = {};

  days.forEach((day) => {
    const dateObj = new Date(day.date);
    const monthKey = dateObj.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    if (!map[monthKey]) map[monthKey] = [];
    map[monthKey].push(day);
  });

  return map;
};

// add leading empty cells
const getMonthCells = (days) => {
  if (!days.length) return [];

  const firstDate = new Date(days[0].date);
  const startDay = firstDate.getDay(); // Sunday start

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  days.forEach((d) => cells.push(d));

  return cells;
};

const formatHumanDate = (dateKey) => {
  const dateObj = new Date(dateKey);
  return dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/* ======================================================
   COMPONENT
====================================================== */

export default function CalendarPage({
  completedDays = {},
  reminderTime,
  flatDays,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeDay, setActiveDay] = useState(null);

  /* ---------------- Calendar Days (FINAL SOURCE) ---------------- */
  const calendarDays = useMemo(() => {
    if (!flatDays?.length) return [];

    const todayKey = getLocalDateKey();

    return flatDays.map((day) => {
      let status = "pending";

      if (completedDays?.[day.date]) status = "completed";
      else if (day.date === todayKey) status = "today";
      else if (day.date < todayKey) status = "missed";

      return {
        ...day,
        status,
        time: reminderTime,
      };
    });
  }, [flatDays, completedDays, reminderTime]);

  const lastDayNumber =
    calendarDays.length > 0
      ? calendarDays[calendarDays.length - 1].dayNumber
      : null;

  const stats = useMemo(() => {
    const summary = {
      total: calendarDays.length,
      completed: 0,
      today: 0,
      missed: 0,
      upcoming: 0,
    };

    calendarDays.forEach((day) => {
      if (day.status === "completed") summary.completed += 1;
      else if (day.status === "today") summary.today += 1;
      else if (day.status === "missed") summary.missed += 1;
      else summary.upcoming += 1;
    });

    return summary;
  }, [calendarDays]);

  const monthGroups = useMemo(
    () => groupDaysByMonth(calendarDays),
    [calendarDays]
  );

  /* ---------------- Firestore ---------------- */
  const markDayComplete = async (day) => {
    if (!user) return;

    await setDoc(
      doc(db, "roadmaps", user.uid),
      {
        completedDays: {
          ...completedDays,
          [day.date]: true,
        },
      },
      { merge: true }
    );
    alert(day.date);
  };

  const isFutureDay = (day) => {
    const today = getLocalDateKey();
    return day.date > today;
  };

  /* ---------------- Render ---------------- */
  return (
    <Page>
      <HeaderShell>
        <HeaderTop>
          <div>
            <Eyebrow>
              <Sparkles size={14} />
              Roadmap Calendar View
            </Eyebrow>
            <Title>Plan your roadmap like a real execution system</Title>
            <HeaderSub>
              See every roadmap day on a real calendar, spot missed momentum
              quickly, and jump straight into the exact lesson or task board you
              need next.
            </HeaderSub>
          </div>

          <FocusPanel>
            <FocusLabel>Calendar Sync</FocusLabel>
            <FocusValue>{reminderTime || "07:30"}</FocusValue>
            <FocusHint>
              Your daily roadmap reminder time for planning and consistency.
            </FocusHint>
          </FocusPanel>
        </HeaderTop>

        <StatsGrid>
          <StatCard>
            <StatLabel>
              <CalendarDays size={14} />
              Total days
            </StatLabel>
            <StatValue>{stats.total}</StatValue>
            <StatHint>Every scheduled learning day across the roadmap.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <CheckCircle2 size={14} />
              Completed
            </StatLabel>
            <StatValue>{stats.completed}</StatValue>
            <StatHint>Days already finished and marked done.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <Flame size={14} />
              Active today
            </StatLabel>
            <StatValue>{stats.today}</StatValue>
            <StatHint>Current day that deserves your main focus.</StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>
              <AlertTriangle size={14} />
              Missed
            </StatLabel>
            <StatValue>{stats.missed}</StatValue>
            <StatHint>Use these as recovery opportunities, not guilt.</StatHint>
          </StatCard>
        </StatsGrid>

        <LegendRow>
          <LegendPill $tone="today">Today</LegendPill>
          <LegendPill $tone="completed">Completed</LegendPill>
          <LegendPill $tone="missed">Missed</LegendPill>
          <LegendPill $tone="pending">Upcoming</LegendPill>
          <LegendCopy>
            Tap any day to open the full lesson or task board.
          </LegendCopy>
        </LegendRow>
      </HeaderShell>

      {Object.entries(monthGroups).map(([month, days]) => {
        const monthCells = getMonthCells(days);
        const monthCompleted = days.filter((day) => day.status === "completed").length;
        const monthToday = days.find((day) => day.status === "today");

        return (
          <MonthSection key={month}>
            <MonthHeader>
              <div>
                <MonthTitle>{month}</MonthTitle>
                <MonthSub>
                  {monthCompleted}/{days.length} completed
                  {monthToday ? ` • Day ${monthToday.dayNumber} is live today` : ""}
                </MonthSub>
              </div>

              <MonthBadge>
                <Target size={14} />
                {days.length} roadmap days
              </MonthBadge>
            </MonthHeader>

            <GridScroller>
              <Grid>
                {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                  <DayHeader key={d}>{d}</DayHeader>
                ))}

                {monthCells.map((day, idx) =>
                  day ? (
                    <DayCell
                      key={day.date}
                      status={day.status}
                      isTarget={day.dayNumber === lastDayNumber}
                      onClick={() => setActiveDay(day)}
                    >
                      <TopRow>
                        <DateNumber>{day.date.split("-")[2]}</DateNumber>
                        <DayCount>Day {day.dayNumber}</DayCount>
                      </TopRow>

                      <Topic>{day.title}</Topic>
                      {idx < monthCells.length - 1 && (
                        <MotivationText status={day.status}>
                          {day.status === "completed" && "Marked done"}
                          {day.status === "today" && "Today’s mission"}
                          {day.status === "missed" && "Needs recovery"}
                          {day.status === "pending" &&
                            isFutureDay(day) &&
                            "Upcoming"}
                        </MotivationText>
                      )}
                      {day.dayNumber === lastDayNumber && (
                        <TargetBadge>Target</TargetBadge>
                      )}
                    </DayCell>
                  ) : (
                    <EmptyCell key={`empty-${idx}`} />
                  )
                )}
              </Grid>
            </GridScroller>
          </MonthSection>
        );
      })}

      {/* ================= DAY DETAIL ================= */}
      {activeDay && (
        <Overlay status={activeDay.status} onClick={() => setActiveDay(null)}>
          <DetailCard onClick={(e) => e.stopPropagation()}>
              <DetailHeader>
                <HeaderLeft>
                  <strong>
                  {activeDay.status === "completed" && "🎉 Well Done!"}
                  {activeDay.status === "today" && "🚀 Today’s Focus"}
                  {activeDay.status === "missed" && "⚠ Let’s Fix This"}
                  {activeDay.status === "pending" && "🔒 Upcoming Lesson"}
                  </strong>
                <DateText>{formatHumanDate(activeDay.date)}</DateText>
              </HeaderLeft>

              <CloseBtn onClick={() => setActiveDay(null)}>✕</CloseBtn>
            </DetailHeader>

            <DetailBody>
              <TopicTitle>{activeDay.title}</TopicTitle>

              <MotivationText status={activeDay.status}>
                {activeDay.status === "completed" &&
                  "You showed up. Consistency is built like this."}
                {activeDay.status === "today" &&
                  "One focused session today moves you ahead."}
                {activeDay.status === "missed" &&
                  "Missed days don’t matter. Returning does."}
                {activeDay.status === "pending" &&
                  "This lesson unlocks soon. Stay ready."}
              </MotivationText>

              <MetaRow>
                <StatusTag status={activeDay.status}>
                  {activeDay.status.toUpperCase()}
                </StatusTag>
                <StatusTag status="pending">DAY {activeDay.dayNumber}</StatusTag>
              </MetaRow>
            </DetailBody>

            <DetailActions>
              {/* View Learning Content */}
              <PrimaryBtn
                onClick={() => {
                  if (activeDay?.dayId) {
                    navigate(`/roadmap/day?dayId=${activeDay.dayId}`);
                  }
                }}
              >
                📘 View Learning Content
              </PrimaryBtn>

              {/* View Task Board (future ready) */}
              <SecondaryBtn
                onClick={() => {
                  navigate(`/tasks-board/day?dayId=${activeDay.dayId}`);
                }}
              >
                🧩 View Task Board
              </SecondaryBtn>

              {/* Mark Complete */}
              {!isFutureDay(activeDay) && activeDay.status !== "completed" && (
                <PrimaryBtn
                  style={{ background: "#22c55e", color: "#020617" }}
                  onClick={() => {
                    markDayComplete(activeDay);
                    setActiveDay(null);
                  }}
                >
                  ✔ Mark Day Complete
                </PrimaryBtn>
              )}
            </DetailActions>
          </DetailCard>
        </Overlay>
      )}
    </Page>
  );
}

/* ======================================================
   STYLES (UNCHANGED)
====================================================== */
const MotivationText = styled.div`
  font-size: 11px;
  font-weight: 800;
  margin-top: 6px;

  color: ${({ status }) =>
    status === "completed"
      ? "#22c55e"
      : status === "today"
      ? "#3b82f6"
      : status === "missed"
      ? "#ef4444"
      : "#94a3b8"};
`;

const Page = styled.div`
  padding: 116px 20px 64px;
  min-height: 100vh;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const HeaderShell = styled.div`
  max-width: 1140px;
  margin: 0 auto 28px;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);

  @media (max-width: 768px) {
    padding: 22px 18px;
    border-radius: 24px;
  }
`;

const HeaderTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.9fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 14px 0 0;
  font-size: 38px;
  line-height: 1.05;
  font-weight: 1000;
  color: #f8fafc;
`;

const HeaderSub = styled.p`
  margin: 12px 0 0;
  max-width: 760px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
`;

const FocusPanel = styled.div`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const FocusLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FocusValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1;
  font-weight: 1000;
`;

const FocusHint = styled.div`
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.6;
`;

const StatsGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1;
  font-weight: 1000;
`;

const StatHint = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;

const LegendRow = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const LegendPill = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  color: ${({ $tone }) =>
    $tone === "today"
      ? "#fde68a"
      : $tone === "completed"
      ? "#bbf7d0"
      : $tone === "missed"
      ? "#fecaca"
      : "#cbd5e1"};
  background: ${({ $tone }) =>
    $tone === "today"
      ? "rgba(245, 158, 11, 0.14)"
      : $tone === "completed"
      ? "rgba(34, 197, 94, 0.14)"
      : $tone === "missed"
      ? "rgba(239, 68, 68, 0.14)"
      : "rgba(148, 163, 184, 0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "today"
        ? "rgba(245, 158, 11, 0.22)"
        : $tone === "completed"
        ? "rgba(34, 197, 94, 0.22)"
        : $tone === "missed"
        ? "rgba(239, 68, 68, 0.22)"
        : "rgba(148, 163, 184, 0.18)"};
`;

const LegendCopy = styled.div`
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
`;
const MonthSection = styled.div`
  max-width: 1140px;
  margin: 0 auto 32px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;

const MonthHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 14px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MonthTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #f8fafc;
`;

const MonthSub = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
`;

const MonthBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
`;

const GridScroller = styled.div`
  overflow-x: auto;
  padding-bottom: 4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(44px, 1fr));
  gap: 10px;
  min-width: 720px;
`;
const DayHeader = styled.div`
  text-align: center;
  font-weight: 700;
  color: #64748b;
  padding-bottom: 6px;
`;
// const DayCell = styled.div`
//   min-height: 110px;
//   border-radius: 14px;
//   padding: 10px;
//   border: 2px solid
//     ${({ isTarget }) => (isTarget ? "#f59e0b" : "rgba(148,163,184,0.15)")};
//   background: ${({ status }) =>
//     status === "completed"
//       ? "rgba(34,197,94,0.18)"
//       : status === "today"
//       ? "rgba(59,130,246,0.22)"
//       : status === "missed"
//       ? "rgba(239,68,68,0.15)"
//       : "rgba(15,23,42,0.6)"};
//   display: flex;
//   flex-direction: column;
//   justify-content: space-between;
// `;
const DayCell = styled.div`
  min-height: 120px;
  border-radius: 16px;
  padding: 12px;
  cursor: pointer;
  border: 2px solid
    ${({ isTarget }) => (isTarget ? "#f59e0b" : "rgba(148,163,184,0.15)")};

  background: ${({ status }) =>
    status === "completed"
      ? "linear-gradient(135deg, rgba(34,197,94,0.25), rgba(15,23,42,0.7))"
      : status === "today"
      ? "linear-gradient(135deg, rgba(59,130,246,0.35), rgba(15,23,42,0.7))"
      : status === "missed"
      ? "linear-gradient(135deg, rgba(239,68,68,0.25), rgba(15,23,42,0.7))"
      : "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(15,23,42,0.6))"};

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  box-shadow: ${({ status }) =>
    status === "completed"
      ? "0 0 0 1px rgba(34,197,94,0.6)"
      : status === "today"
      ? "0 0 0 1px rgba(59,130,246,0.6)"
      : "none"};

  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.26);
  }
`;

const EmptyCell = styled.div`
  min-height: 90px;
`;
const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
`;
const DateNumber = styled.div`
  font-size: 20px;
  font-weight: 900;
`;
const DayCount = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;
const Topic = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const TargetBadge = styled.div`
  font-size: 11px;
  font-weight: 900;
  padding: 4px 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, #f59e0b, #ef4444);
  color: #020617;
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;

  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  background: ${({ status }) =>
    status === "completed"
      ? "linear-gradient(180deg, rgba(34,197,94,0.25), rgba(2,6,23,0.85))"
      : status === "today"
      ? "linear-gradient(180deg, rgba(59,130,246,0.25), rgba(2,6,23,0.85))"
      : status === "missed"
      ? "linear-gradient(180deg, rgba(239,68,68,0.22), rgba(2,6,23,0.85))"
      : "linear-gradient(180deg, rgba(99,102,241,0.22), rgba(2,6,23,0.85))"};

  transition: background 0.25s ease;
`;

const DetailCard = styled.div`
  width: 92%;
  max-width: 640px;

  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  border-radius: 20px;
  padding: 20px;

  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);

  animation: scaleIn 0.18s ease;

  @media (max-width: 640px) {
    width: calc(100% - 24px);
    padding: 18px;
  }

  @keyframes scaleIn {
    from {
      transform: scale(0.96);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;
const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;
const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #cbd5e1;
  cursor: pointer;
`;
const DetailBody = styled.div`
  margin-top: 14px;
`;
const TopicTitle = styled.h3`
  font-size: 18px;
  font-weight: 900;
  margin-top: 10px;
  line-height: 1.3;

  color: #e5e7eb;
`;
const MetaRow = styled.div`
  display: flex;
  gap: 8px;
`;
const StatusTag = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${({ status }) =>
    status === "completed"
      ? "#22c55e"
      : status === "today"
      ? "#3b82f6"
      : status === "missed"
      ? "#ef4444"
      : "#64748b"};
  color: #020617;
`;
const DetailActions = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 10px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;
const PrimaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-weight: 800;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
`;
const SecondaryBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(15, 23, 42, 0.74);
  color: #e2e8f0;
  cursor: pointer;
`;
const DateText = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;
